import { useClient } from '@/api/hooks';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { apiFetch, getErrorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { Check, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

type BillingStatus = {
  configured: boolean;
  plan_code: string;
  status: string;
  stripe_status: string | null;
  has_stripe_customer: boolean;
  plan_started_at: string | null;
  plan_expires_at: string | null;
  stripe_current_period_end: string | null;
  trial_days: number;
  blocked: boolean;
  plans: Array<{ code: string; name: string; price: number | null; period: string }>;
};

const PLAN_FEATURES: Record<string, string[]> = {
  trial: ['1 propriedade', 'Até 100 animais', 'Suporte por e-mail'],
  essencial: ['3 propriedades', 'Animais ilimitados', 'App mobile', 'Suporte prioritário'],
  profissional: [
    'Propriedades ilimitadas',
    'Múltiplos usuários',
    'Importação em massa',
    'Relatórios avançados',
  ],
};

export function SubscriptionPage() {
  const { data: client, refetch: refetchClient } = useClient();
  const [searchParams] = useSearchParams();
  const checkoutResult = searchParams.get('checkout');
  const [selected, setSelected] = useState('essencial');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const { data: billing, refetch: refetchBilling } = useQuery({
    queryKey: ['billing-status'],
    queryFn: () => apiFetch<BillingStatus>('/billing/status'),
  });

  const currentPlan = billing?.plan_code ?? client?.plan_code ?? 'trial';
  const plans = useMemo(() => {
    const fromApi = billing?.plans ?? [];
    if (fromApi.length) {
      return fromApi.map((p) => ({
        ...p,
        description:
          p.code === 'trial'
            ? 'Ideal para conhecer o sistema'
            : p.code === 'essencial'
              ? 'Pequenas propriedades e recria'
              : 'Operação completa com equipe',
        features: PLAN_FEATURES[p.code] ?? [],
        highlighted: p.code === 'essencial',
      }));
    }
    return [
      {
        code: 'trial',
        name: 'Trial',
        price: 0,
        period: '30 dias',
        description: 'Ideal para conhecer o sistema',
        features: PLAN_FEATURES.trial,
        highlighted: false,
      },
      {
        code: 'essencial',
        name: 'Essencial',
        price: 149,
        period: '/mês',
        description: 'Pequenas propriedades e recria',
        features: PLAN_FEATURES.essencial,
        highlighted: true,
      },
      {
        code: 'profissional',
        name: 'Profissional',
        price: 349,
        period: '/mês',
        description: 'Operação completa com equipe',
        features: PLAN_FEATURES.profissional,
        highlighted: false,
      },
    ];
  }, [billing?.plans]);

  async function startCheckout(planCode: string) {
    setError('');
    setBusy(true);
    try {
      const res = await apiFetch<{ url: string }>('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ plan_code: planCode }),
      });
      window.location.href = res.url;
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível iniciar o checkout.'));
      setBusy(false);
    }
  }

  async function openPortal() {
    setError('');
    setBusy(true);
    try {
      const res = await apiFetch<{ url: string }>('/billing/portal', { method: 'POST' });
      window.location.href = res.url;
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível abrir o portal de cobrança.'));
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Assinatura"
        subtitle="Escolha o plano ideal para sua operação"
      />

      {checkoutResult === 'success' && (
        <Alert variant="success" className="mb-6">
          Pagamento iniciado com sucesso. A assinatura pode levar alguns segundos para atualizar.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              void refetchBilling();
              void refetchClient();
            }}
          >
            Atualizar status
          </button>
        </Alert>
      )}
      {checkoutResult === 'cancel' && (
        <Alert variant="info" className="mb-6">
          Checkout cancelado. Você pode tentar novamente quando quiser.
        </Alert>
      )}
      {billing?.blocked && (
        <Alert variant="error" className="mb-6">
          Sua assinatura está inativa ou o trial expirou. Assine um plano para continuar usando o
          sistema.
        </Alert>
      )}
      {!billing?.configured && (
        <Alert variant="info" className="mb-6">
          Pagamentos Stripe ainda não estão configurados neste ambiente. Os planos abaixo são
          informativos.
        </Alert>
      )}
      {error && (
        <Alert variant="error" className="mb-6">
          {error}
        </Alert>
      )}

      <Card className="mb-6">
        <CardBody className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm text-manejai-bark/60">Plano atual</p>
            <p className="font-display text-lg font-semibold text-manejai-forest capitalize">
              {currentPlan}
            </p>
            <p className="text-xs text-manejai-bark/55 mt-1">
              Status conta: {billing?.status ?? client?.status ?? '—'}
              {billing?.stripe_status ? ` · Stripe: ${billing.stripe_status}` : ''}
            </p>
            {billing?.plan_expires_at && (
              <p className="text-xs text-manejai-bark/55">
                {currentPlan === 'trial' ? 'Trial até' : 'Período até'}:{' '}
                {formatDate(billing.plan_expires_at)}
              </p>
            )}
          </div>
          {billing?.has_stripe_customer && (
            <Button type="button" variant="secondary" onClick={() => void openPortal()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              Gerenciar assinatura
            </Button>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3 mb-8">
        {plans.map((plan) => (
          <Card
            key={plan.code}
            className={
              selected === plan.code
                ? 'ring-2 ring-manejai-sage shadow-elevated'
                : plan.highlighted
                  ? 'border-manejai-clay/40'
                  : ''
            }
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-semibold text-manejai-forest">{plan.name}</h3>
                  <p className="text-xs text-manejai-bark/60 mt-1">{plan.description}</p>
                </div>
                {currentPlan === plan.code && <Badge tone="success">Atual</Badge>}
                {plan.highlighted && currentPlan !== plan.code && (
                  <Badge tone="warning">Popular</Badge>
                )}
              </div>
              <p className="mt-4">
                <span className="font-display text-3xl font-semibold text-manejai-forest">
                  {plan.price === 0 || plan.price == null
                    ? 'Grátis'
                    : `R$ ${Number(plan.price).toFixed(0)}`}
                </span>
                <span className="text-sm text-manejai-bark/55 ml-1">{plan.period}</span>
              </p>
            </CardHeader>
            <CardBody>
              <ul className="space-y-2 mb-4">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-manejai-bark/80">
                    <Check className="h-4 w-4 text-manejai-sage shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              {plan.code === 'trial' ? (
                <Button type="button" variant="secondary" className="w-full" disabled>
                  Incluso no cadastro
                </Button>
              ) : currentPlan === plan.code && billing?.has_stripe_customer ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => void openPortal()}
                  disabled={busy}
                >
                  Gerenciar no Stripe
                </Button>
              ) : (
                <Button
                  type="button"
                  className="w-full"
                  disabled={busy || !billing?.configured}
                  onClick={() => {
                    setSelected(plan.code);
                    void startCheckout(plan.code);
                  }}
                >
                  {busy && selected === plan.code ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  Assinar {plan.name}
                </Button>
              )}
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}
